import { supabase } from './supabase';
import { ErrorHandler, ErrorCategories, ErrorSeverity, withErrorHandling } from '../utils/errorHandling';

export interface CheckoutSessionRequest {
  priceId: string;
  mode: 'payment' | 'subscription';
  successUrl?: string;
  cancelUrl?: string;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface SubscriptionData {
  customer_id: string;
  subscription_id: string | null;
  subscription_status: string;
  price_id: string | null;
  current_period_start: number | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

export const createCheckoutSession = withErrorHandling(async (
  request: CheckoutSessionRequest
): Promise<CheckoutSessionResponse> => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError || !session?.access_token) {
    throw new Error('User not authenticated');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const apiUrl = `${supabaseUrl}/functions/v1/stripe-checkout`;

  const defaultSuccessUrl = `${window.location.origin}/success`;
  const defaultCancelUrl = `${window.location.origin}/commitment`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      'X-Client-Info': 'manivo-web-app'
    },
    body: JSON.stringify({
      price_id: request.priceId,
      mode: request.mode,
      success_url: request.successUrl || defaultSuccessUrl,
      cancel_url: request.cancelUrl || defaultCancelUrl,
    }),
  });

  if (!response.ok) {
    let errorMessage = 'Failed to create checkout session';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch (parseError) {
      // If we can't parse the error response, use the default message
    }
    
    const error = new Error(errorMessage);
    ErrorHandler.getInstance().handleError(
      error,
      { 
        component: 'stripe', 
        action: 'createCheckoutSession',
        additionalData: { 
          priceId: request.priceId, 
          mode: request.mode,
          responseStatus: response.status 
        }
      },
      ErrorSeverity.HIGH,
      ErrorCategories.PAYMENT
    );
    throw error;
  }

  return await response.json();
}, { component: 'stripe', action: 'createCheckoutSession' }, { maxRetries: 2 });

export const getUserSubscription = withErrorHandling(async (): Promise<SubscriptionData | null> => {
  const { data, error } = await supabase
    .from('stripe_user_subscriptions')
    .select('*')
    .maybeSingle();

  if (error) {
    ErrorHandler.getInstance().handleError(
      new Error(error.message),
      { component: 'stripe', action: 'getUserSubscription' },
      ErrorSeverity.MEDIUM,
      ErrorCategories.DATABASE
    );
    throw error;
  }

  return data;
}, { component: 'stripe', action: 'getUserSubscription' });

export const getUserOrders = withErrorHandling(async () => {
  const { data, error } = await supabase
    .from('stripe_user_orders')
    .select('*')
    .order('order_date', { ascending: false });

  if (error) {
    ErrorHandler.getInstance().handleError(
      new Error(error.message),
      { component: 'stripe', action: 'getUserOrders' },
      ErrorSeverity.MEDIUM,
      ErrorCategories.DATABASE
    );
    throw error;
  }

  return data;
}, { component: 'stripe', action: 'getUserOrders' });